/**
 * The Linked repositories view model (Settings → Context, ACT2-1133). `warehouseProjects.list` returns
 * one row per (repository, path, account key); the list shows one row per PROJECT, a repository and a
 * path, so the rows are grouped here, on read. Pure: no React.
 *
 * A view model with no stored counterpart (AGENTS.md "Project Layout", case (b)): the rows themselves
 * are the backend's type.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

import type { FilterGroup } from '@/_shared/components/list/ListToolbar';
import type { ListDeriveConfig, SortState } from '@/_shared/lib/list-controls';

import { projectTypeLabel } from './projectType';

/** One `warehouseProjects.list` row: an admin gets the whole row, a member its member-safe fields. */
export type LinkedRepoRow = Awaited<
  ReturnType<RouterClient<AppRouter>['warehouseProjects']['list']>
>['projects'][number];

/** The account fields the view reads, from `settings.warehouses.listAccounts` (named). */
export interface LinkedRepoAccount {
  id: string;
  accountKey: string;
  name: string;
  type: string;
}

export interface AccountKeyDisplay {
  /** The key's type part (`SNOWFLAKE`, `BQ`, …), for the warehouse icon and label. */
  type: string;
  /** The database part: what a person calls the key ("PROD"). */
  database: string;
  host: string;
  raw: string;
}

/**
 * A key read as a person would say it. Splits `TYPE|host|database` locally, the format
 * `deriveAccountKey` builds (`src/lib/warehouse-keys.ts`); `catalogNameFromAccountKey` there reads the
 * same part, but that module pulls the warehouse strategies into the bundle.
 */
export function accountKeyDisplay(key: string): AccountKeyDisplay {
  const parts = key.split('|');
  if (parts.length !== 3) return { type: '', database: key, host: '', raw: key };
  const [type = '', host = '', database = ''] = parts;
  return { type, database: database || host || key, host, raw: key };
}

export type DocState = LinkedRepoRow['docState'];

/** A compile is running or scheduled for the key's doc. */
export function isDocBusy(docState: DocState): boolean {
  return docState?.status === 'generating';
}

export interface LinkedProjectKey {
  accountKey: string;
  projectId: string;
  accountStatus: 'connected' | 'disconnected';
  docState: DocState;
  /** The accounts that read this key; empty when it is Disconnected. */
  accounts: { id: string; name: string }[];
  display: AccountKeyDisplay;
}

export type LinkedProjectStatus = 'connected' | 'disconnected' | 'mixed';

export interface LinkedProject {
  /** `${provider}|${repoFullName}|${projectPath}`. */
  groupId: string;
  provider: string;
  repoFullName: string;
  projectPath: string;
  installationId: number | null;
  /** The known type its rows share, "Mixed" when they differ. */
  projectTypeLabel: string;
  /** Sorted by database name. */
  keys: LinkedProjectKey[];
  /** "PROD, DEV"; a Disconnected key reads "PROD (removed)". */
  keysLabel: string;
  status: LinkedProjectStatus;
  /** "Connected" | "Disconnected" | "2 connected · 1 disconnected". */
  statusSummary: string;
  anyDisconnected: boolean;
  anyDocBusy: boolean;
}

function byDatabase(a: LinkedProjectKey, b: LinkedProjectKey): number {
  return (
    a.display.database.localeCompare(b.display.database, 'en', { sensitivity: 'base' }) ||
    a.accountKey.localeCompare(b.accountKey, 'en')
  );
}

export function projectGroupId(row: Pick<LinkedRepoRow, 'provider' | 'repoFullName' | 'projectPath'>): string {
  return `${row.provider}|${row.repoFullName}|${row.projectPath}`;
}

/** The accounts reading each key, by `accountKey`. */
export function accountsByKey(accounts: readonly LinkedRepoAccount[]): Map<string, LinkedRepoAccount[]> {
  const byKey = new Map<string, LinkedRepoAccount[]>();
  for (const account of accounts) {
    const list = byKey.get(account.accountKey) ?? [];
    list.push(account);
    byKey.set(account.accountKey, list);
  }
  return byKey;
}

function statusOf(keys: readonly LinkedProjectKey[]): { status: LinkedProjectStatus; summary: string } {
  const disconnected = keys.filter((k) => k.accountStatus === 'disconnected').length;
  const connected = keys.length - disconnected;
  if (disconnected === 0) return { status: 'connected', summary: 'Connected' };
  if (connected === 0) return { status: 'disconnected', summary: 'Disconnected' };
  return { status: 'mixed', summary: `${connected} connected · ${disconnected} disconnected` };
}

/** One project per (provider, repository, path), in the order the server first lists them. */
export function groupLinkedProjects(
  rows: readonly LinkedRepoRow[],
  accounts: readonly LinkedRepoAccount[],
): LinkedProject[] {
  const byKey = accountsByKey(accounts);
  const groups = new Map<string, LinkedRepoRow[]>();
  for (const row of rows) {
    const id = projectGroupId(row);
    const list = groups.get(id) ?? [];
    list.push(row);
    groups.set(id, list);
  }
  return [...groups.entries()].map(([groupId, group]) => {
    const [first] = group as [LinkedRepoRow, ...LinkedRepoRow[]];
    const keys = group
      .map(
        (row): LinkedProjectKey => ({
          accountKey: row.accountKey,
          projectId: row.id,
          accountStatus: row.accountStatus,
          docState: row.docState,
          accounts: (byKey.get(row.accountKey) ?? []).map(({ id, name }) => ({ id, name })),
          display: accountKeyDisplay(row.accountKey),
        }),
      )
      .sort(byDatabase);
    const types = new Set(group.map((row) => row.projectType));
    const { status, summary } = statusOf(keys);
    return {
      groupId,
      provider: first.provider,
      repoFullName: first.repoFullName,
      projectPath: first.projectPath,
      installationId: first.installationId,
      projectTypeLabel: types.size === 1 ? projectTypeLabel(first.projectType) : 'Mixed',
      keys,
      keysLabel: keys
        .map((k) => (k.accountStatus === 'disconnected' ? `${k.display.database} (removed)` : k.display.database))
        .join(', '),
      status,
      statusSummary: summary,
      anyDisconnected: status !== 'connected',
      anyDocBusy: keys.some((k) => isDocBusy(k.docState)),
    };
  });
}

/** `/` reads as the repository root. */
export function projectPathLabel(projectPath: string): string {
  return projectPath === '' ? '/' : projectPath;
}

/** "owner/repo (/dbt)", for dialog copy. */
export function projectLabel(project: Pick<LinkedProject, 'repoFullName' | 'projectPath'>): string {
  return `${project.repoFullName} (${projectPathLabel(project.projectPath)})`;
}

// ─── Account key picker ──────────────────────────────────────────────────────

export interface AccountKeyOption {
  accountKey: string;
  display: AccountKeyDisplay;
  accounts: { id: string; name: string }[];
  /** Linked by the project, but no account has it: it can only be unticked. */
  removed: boolean;
}

/**
 * The picker's options: every distinct key among the accounts, plus (Edit) the project's linked keys
 * that have no account. A key with no account is never offered to add.
 */
export function accountKeyOptions(
  accounts: readonly LinkedRepoAccount[],
  linked: readonly LinkedProjectKey[] = [],
): AccountKeyOption[] {
  const options: AccountKeyOption[] = [...accountsByKey(accounts).entries()].map(([accountKey, list]) => ({
    accountKey,
    display: accountKeyDisplay(accountKey),
    accounts: list.map(({ id, name }) => ({ id, name })),
    removed: false,
  }));
  const known = new Set(options.map((o) => o.accountKey));
  for (const key of linked) {
    if (known.has(key.accountKey)) continue;
    options.push({ accountKey: key.accountKey, display: key.display, accounts: [], removed: true });
  }
  return options.sort(
    (a, b) =>
      a.display.database.localeCompare(b.display.database, 'en', { sensitivity: 'base' }) ||
      a.accountKey.localeCompare(b.accountKey, 'en'),
  );
}

/** "a", "a and b", "a, b and c". */
function listJoin(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The note above Edit's Save: which keys' docs regenerate and which are deleted. A best guess from the
 * list; the server decides. Added keys compile; with a new path every kept key recompiles; a removed
 * key's doc recompiles when another project still links it, else it is deleted. Null when nothing changes.
 */
export function editProjectNote(
  project: LinkedProject,
  nextKeys: readonly string[],
  pathChanged: boolean,
  allProjects: readonly LinkedProject[],
): string | null {
  const current = new Set(project.keys.map((k) => k.accountKey));
  const next = new Set(nextKeys);
  const name = (key: string): string => accountKeyDisplay(key).database;
  const linkedElsewhere = (key: string): boolean =>
    allProjects.some((p) => p.groupId !== project.groupId && p.keys.some((k) => k.accountKey === key));

  const regenerated: string[] = [];
  const deleted: string[] = [];
  for (const key of nextKeys) {
    if (!current.has(key) || pathChanged) regenerated.push(name(key));
  }
  for (const key of current) {
    if (next.has(key)) continue;
    if (linkedElsewhere(key)) regenerated.push(name(key));
    else deleted.push(name(key));
  }
  const sentences: string[] = [];
  if (regenerated.length > 0) sentences.push(`Docs for ${listJoin(regenerated)} are regenerated.`);
  if (deleted.length > 0) {
    sentences.push(
      deleted.length === 1
        ? `Docs for ${deleted[0]} are deleted (no other repository uses it).`
        : `Docs for ${listJoin(deleted)} are deleted (no other repository uses them).`,
    );
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

// ─── List controls ───────────────────────────────────────────────────────────

export type LinkedRepoSortKey = 'repository';
export type LinkedRepoFilterKey = 'status';

export const LINKED_REPO_SORT_KEYS: readonly LinkedRepoSortKey[] = ['repository'];
export const LINKED_REPO_FILTER_KEYS: readonly LinkedRepoFilterKey[] = ['status'];
export const LINKED_REPO_DEFAULT_SORT: SortState<LinkedRepoSortKey> = { key: 'repository', dir: 'asc' };
export const LINKED_REPO_SORT_OPTIONS = [{ value: 'repository', label: 'Repository' }] as const;

/** Connected: every key is. Disconnected: at least one key is. */
function inStatusBucket(project: LinkedProject, value: string): boolean {
  if (value === 'connected') return !project.anyDisconnected;
  if (value === 'disconnected') return project.anyDisconnected;
  return true;
}

/** The Status filter, with project counts per bucket; none while there is nothing to tell apart. */
export function linkedRepoFilterGroups(projects: readonly LinkedProject[]): FilterGroup[] {
  if (projects.length === 0) return [];
  const connected = projects.filter((p) => inStatusBucket(p, 'connected')).length;
  const disconnected = projects.length - connected;
  return [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'connected', label: `Connected (${connected})` },
        { value: 'disconnected', label: `Disconnected (${disconnected})` },
      ],
    },
  ];
}

export const LINKED_REPO_DERIVE_CONFIG: ListDeriveConfig<LinkedProject, LinkedRepoSortKey, LinkedRepoFilterKey> = {
  searchText: (p) => [p.repoFullName, p.projectPath, ...p.keys.map((k) => k.display.database)],
  comparators: {
    repository: (a, b) =>
      a.repoFullName.localeCompare(b.repoFullName, 'en', { sensitivity: 'base' }) ||
      a.projectPath.localeCompare(b.projectPath, 'en'),
  },
  filterPredicates: { status: inStatusBucket },
};
